import { useTranslation } from "@redotech/redo-i18n";
import type { ReturnTypeState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { memo, useMemo } from "react";

function returnTypeLabel(
  value: ReturnTypeEnum,
  t: (key: string) => string,
): string {
  switch (value) {
    case ReturnTypeEnum.RETURN:
      return t("Return");
    case ReturnTypeEnum.CLAIM:
      return t("Claim");
    case ReturnTypeEnum.WARRANTY:
      return t("Warranty");
    case ReturnTypeEnum.RECYCLE:
      return t("Recycle");
    case ReturnTypeEnum.WITHDRAWAL:
      return t("Withdrawal");
  }
}

export const ReturnTypeDetails = memo(function ReturnTypeDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<ReturnTypeState>) {
  const { t } = useTranslation();
  const returnTypeOptions = useMemo<BadgeRedoListItem<ReturnTypeEnum>[]>(
    () =>
      Object.values(ReturnTypeEnum).map((value) => ({
        id: value,
        value,
        type: "badge" as const,
        badge: { text: returnTypeLabel(value, t), color: "gray" as const },
      })),
    [t],
  );

  const selectedOptions = useMemo(
    () =>
      returnTypeOptions.filter((option) =>
        state.returnTypes.includes(option.value),
      ),
    [returnTypeOptions, state.returnTypes],
  );

  return (
    <RedoTagsDropdownInput
      disabled={disabled}
      label={t("Return Types")}
      options={returnTypeOptions}
      placeholder={t("Select return types...")}
      selectedOptions={selectedOptions}
      setSelectedOptions={(items: BadgeRedoListItem<ReturnTypeEnum>[]) => {
        setState({ returnTypes: items.map((item) => item.value) });
      }}
      size="xs"
    />
  );
});
